/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import Features, {_restrictedAccountAllowedFeatures, ADMIN_GROUPS_PAGE} from '../security/Features';
import {getFeatureByRoute, getRoute, getRouteDefinition, Route} from './RouteDefinitions';

/**
 * Returns the default page ID for restricted accounts if the current
 * account is restricted, otherwise, returns the specified page ID.
 *
 * ```typescript
 *
 * import {getDefaultPageId} from '@/routes/RouteSecurity';
 *
 * const id = getDefaultPageId(TRACKING_PAGE);
 *
 * ```
 *
 */
export const getDefaultPageId = (defaultPageId?: string): string => {
  return Features._isCurrentAccountRestricted
    ? _restrictedAccountAllowedFeatures.includes(defaultPageId)
      ? defaultPageId : ADMIN_GROUPS_PAGE : defaultPageId;
};

/**
 * Returns the true value if restricted accounts having the access to
 * given path. else returns false.
 *
 * ```typescript
 *
 * import {isRestrictedAccountAllowedPage} from '@/routes/RouteSecurity';
 *
 * const page = isRestrictedAccountAllowedPage(getRoute(Route.TRACKING));
 *
 * ```
 *
 */
export const isRestrictedAccountAllowedPage = (pageId: Route | string, isPath = true): boolean => {
  const pageFeature = getFeatureByRoute(isPath ? getRouteDefinition(pageId) : pageId);
  return _restrictedAccountAllowedFeatures.includes(pageFeature);
};

/**
 * Returns the default page for restricted accounts if the current
 * account is restricted, otherwise, returns the specified page.
 *
 * ```typescript
 *
 * import {getDefaultPage} from '@/routes/RouteSecurity';
 *
 * const page = getDefaultPage(getRoute(Route.TRACKING));
 *
 * ```
 *
 */
export const getDefaultPage = (defaultPage?: string): string => {
  let page = defaultPage;
  if (Features._isCurrentAccountRestricted) {
    const hasAccess = isRestrictedAccountAllowedPage(defaultPage);
    !hasAccess && (page = getRoute(Route.ADMIN_GROUPS));
  }
  return page;
};

/**
 * Returns the default route for restricted accounts if the current account
 * is restricted, otherwise, returns the specified route.
 *
 * ```typescript
 *
 * import {getDefaultRoute} from '@/routes/RouteSecurity';
 *
 * const route = getDefaultRoute(Route.TRACKING);
 *
 * ```
 *
 */
export const getDefaultRoute = (defaultRoute?: Route): Route => {
  let route = defaultRoute;
  if (Features._isCurrentAccountRestricted) {
    const hasAccess = isRestrictedAccountAllowedPage(defaultRoute);
    !hasAccess && (route = Route.ADMIN_GROUPS);
  }
  return route;
};
